import { isWorkflowFinished } from "../constants";
import { buildExportReadiness } from "../project-readiness";
import type { ProjectSummary } from "../types";
import { renderPagePath } from "../view-helpers";

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

export function renderProjectPreviewPage(project: ProjectSummary, pendingSyncCount = 0): string {
  const readiness = buildExportReadiness(project, pendingSyncCount);
  const totalItems = project.inspectionItems.length;
  const finishedItems = project.inspectionItems.filter((item) => isWorkflowFinished(item.workflowStatus)).length;
  const emailAllowed =
    readiness.outputAllowed &&
    Boolean(project.exportStatus.reportGeneratedAt) &&
    Boolean(project.exportStatus.recordGeneratedAt);
  const reportGenerated = Boolean(project.exportStatus.reportGeneratedAt);
  const recordGenerated = Boolean(project.exportStatus.recordGeneratedAt);
  const generationHint = !readiness.outputAllowed
    ? readiness.outputBlockedReason
    : reportGenerated && recordGenerated
      ? "正式报告和正式记录表都已生成，当前可以直接填写邮箱并发送下载链接。"
      : reportGenerated
        ? "正式报告已生成，还差正式记录表；补齐后才能发送到邮箱。"
        : recordGenerated
          ? "正式记录表已生成，还差正式报告；补齐后才能发送到邮箱。"
          : "当前已满足生成条件；先生成正式报告和正式记录表，再填写邮箱发送下载链接。";

  return `
    <section class="screen">
      <header class="topbar">
        <button class="ghost-button" data-action="back-to-home">返回项目</button>
        <h1>成品预览与发送</h1>
      </header>
      ${renderPagePath([
        { label: project.name, href: `#/projects/home?id=${project.id}` },
        { label: "成品预览与发送" },
      ])}
      <div class="panel stack">
        <p class="muted">这里不再展示内容草稿，只保留真正的成品预览、正式生成和邮件发送。现场时你先看成品预览，确认排版和照片都没问题；回到电脑后再下载正式 Word。</p>
        <div class="inline-pills">
          <span class="state-pill">已完成 ${finishedItems}/${totalItems} 个单项</span>
          <span class="state-pill ${pendingSyncCount > 0 ? "is-warning" : ""}">${pendingSyncCount > 0 ? `待同步 ${pendingSyncCount} 项` : "已同步"}</span>
          <span class="state-pill ${reportGenerated ? "" : "is-warning"}">${reportGenerated ? "报告已生成" : "报告未生成"}</span>
          <span class="state-pill ${recordGenerated ? "" : "is-warning"}">${recordGenerated ? "记录表已生成" : "记录表未生成"}</span>
        </div>
        <p class="mini-meta">${escapeHtml(generationHint)}</p>
        <button class="ghost-button" type="button" data-action="sync-project">立即同步</button>
      </div>
      <section class="panel stack">
        <div class="stack">
          <button class="nav-button" type="button" data-action="go-report-preview">预览报告成品</button>
          <button class="nav-button" type="button" data-action="go-record-preview">预览记录表成品</button>
        </div>
        ${
          readiness.outputAllowed
            ? ""
            : `<p class="muted">${escapeHtml(readiness.outputBlockedReason)}</p>`
        }
        <div class="stack">
          <button class="nav-button" type="button" data-action="export-report-docx" ${
            readiness.outputAllowed ? "" : "disabled"
          }>生成正式报告（Word）</button>
          <p class="mini-meta">${reportGenerated ? "正式报告已生成完成。" : "点击后开始生成正式报告。生成过程大约持续 1-3 分钟。"}</p>
          ${
            reportGenerated
              ? `<button class="ghost-button small-button" type="button" data-action="clear-report-export">删除已生成报告</button>`
              : ""
          }
          <button class="nav-button" type="button" data-action="export-record-docx" ${
            readiness.outputAllowed ? "" : "disabled"
          }>生成正式记录表（Word）</button>
          <p class="mini-meta">${recordGenerated ? "正式记录表已生成完成。" : "点击后开始生成正式记录表。生成过程大约持续 1-3 分钟。"}</p>
          ${
            recordGenerated
              ? `<button class="ghost-button small-button" type="button" data-action="clear-record-export">删除已生成记录表</button>`
              : ""
          }
        </div>
        <div class="field">
          <button class="nav-button" type="button" data-action="send-export-email" ${
            emailAllowed ? "" : "disabled"
          }>发送到邮箱</button>
          <input id="preview-email-input" type="email" inputmode="email" placeholder="填写接收邮箱地址" ${
            emailAllowed ? "" : "disabled"
          } />
        </div>
        <p class="mini-meta" id="preview-export-status">${
          emailAllowed
            ? "报告和记录表都已生成成功；发送到邮箱时将只发送 24 小时有效的下载链接，不再直接塞邮箱附件。"
            : readiness.outputAllowed
              ? "先把正式报告和正式记录表都生成出来，再填写邮箱发送下载链接。"
              : "成品预览可随时查看；全部单项达到已完成或不涉及，且当前没有待同步内容后，再开放正式生成与邮箱发送。"
        }</p>
      </section>
    </section>
  `;
}
